import { useClinicalEditors } from './context';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  Check,
  Download,
  Eye,
  ImagePlus,
  Pencil,
  Send,
  Sparkles,
  ZoomIn,
  Scan,
} from 'lucide-react';
import { Button } from '@rudi/ui/button';
import { Textarea } from '@rudi/ui/textarea';
import { getErrorMessage } from './context';
import type {
  PosterDraft,
  PosterSubmissionResponse,
  PosterSubmissionFields,
} from '@rudi/shared/types/clinical-trials';
import { usePublicInvestigator } from './usePublicInvestigator';
import type {
  PiSuggestionResponse,
  PiSuggestionSurface,
  PiSuggestionSurfaceStatus,
} from '@rudi/shared/types/clinical-trials';

import { PosterArtwork, blobToDataUrl, renderPosterCanvas } from './PosterArtwork';
import {
  PosterPortraitEditor,
  DEFAULT_PORTRAIT_CROP,
  type PortraitSource,
} from './PosterPortraitEditor';
import {
  POSTER_STYLES,
  posterBlockFont,
  loadPosterFonts,
  posterT,
  POSTER_W,
  POSTER_H,
  PORTRAIT_FRAME,
  DEFAULT_POSTER_IMAGE,
  fitPosterText,
  initialPosterFields,
  posterTemplateId,
  posterTextBlocks,
  type PosterStyle,
} from './poster-design';

import styles from './PosterTab.module.css';

const A4_W = 595.28;
const A4_H = 841.89;

interface PosterTabProps {
  viewerSlug?: string | null;
  viewerLabel?: string | null;
  demoMode?: boolean;
  operatorMode?: boolean;
  canSubmit?: boolean;
  suggestion?: PiSuggestionResponse | null;
  suggestionUpdating?: boolean;
  onSuggestionStatusChange?: (
    surface: PiSuggestionSurface,
    status: Extract<PiSuggestionSurfaceStatus, 'applied' | 'dismissed'>
  ) => Promise<unknown> | unknown;
  onSuggestionRefresh?: () => Promise<unknown> | unknown;
}

export function PosterTab({
  viewerSlug,
  viewerLabel,
  demoMode = false,
  operatorMode = false,
  canSubmit = !demoMode,
  suggestion = null,
  suggestionUpdating = false,
  onSuggestionStatusChange,
  onSuggestionRefresh,
}: PosterTabProps) {
  const {
    t,
    api: apiClient,
    operations: { createPosterSubmission },
  } = useClinicalEditors();
  const { investigator } = usePublicInvestigator(viewerSlug);
  const [fields, setFields] = useState<PosterSubmissionFields>(() =>
    initialPosterFields('classic', viewerLabel)
  );
  const [posterStyle, setPosterStyle] = useState<PosterStyle>('classic');
  const drafts = useRef<Partial<Record<PosterStyle, PosterSubmissionFields>>>({});
  const [, setFontsReady] = useState(false);
  useEffect(() => {
    if (!document.fonts) return;
    let active = true;
    void loadPosterFonts().then(() => {
      if (active) setFontsReady(true);
    });
    return () => {
      active = false;
    };
  }, []);
  const [enlarged, setEnlarged] = useState(false);
  const [editing, setEditing] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [portraitEditor, setPortraitEditor] = useState<PortraitSource | null>(null);
  const portraitSources = useRef<
    Partial<Record<PosterStyle, { rendered: string; source: PortraitSource }>>
  >({});
  const [focusedText, setFocusedText] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acceptedSuggestions, setAcceptedSuggestions] = useState<
    Partial<Record<PosterStyle, string>>
  >({});
  const [applyingSuggestion, setApplyingSuggestion] = useState(false);
  const versions = useRef<Partial<Record<PosterStyle, PosterDraft | null>>>({});
  const noteDrafts = useRef<Partial<Record<PosterStyle, string>>>({});
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [savedDraft, setSavedDraft] = useState<PosterDraft | null>(null);
  const [reloadDraft, setReloadDraft] = useState(0);
  const [submissions, setSubmissions] = useState<PosterSubmissionResponse[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  useEffect(() => {
    if (!viewerSlug || demoMode) return;
    let active = true;
    apiClient
      .get<PosterSubmissionResponse[]>('/clinical-trials/poster-submissions')
      .then(({ data }) => {
        if (active)
          setSubmissions((data ?? []).filter(row => row.selectedInvestigatorSlug === viewerSlug));
      })
      .catch(cause => {
        if (active) setHistoryError(getErrorMessage(cause));
      });
    return () => {
      active = false;
    };
  }, [apiClient, viewerSlug, demoMode, status]);
  const editSubmission = async (submission: PosterSubmissionResponse) => {
    const style = POSTER_STYLES.find(style =>
      submission.selectedTemplateIds.includes(posterTemplateId(style))
    );
    if (!style || !viewerSlug) return;
    setDraftLoading(true);
    setDraftError(null);
    try {
      const { data } = await apiClient.get<PosterDraft | null>(
        `/clinical-trials/investigators/${encodeURIComponent(viewerSlug)}/poster-drafts/${posterTemplateId(style)}`
      );
      versions.current[style] = data;
      drafts.current[posterStyle] = fields;
      noteDrafts.current[posterStyle] = notes;
      setPosterStyle(style);
      setFields(submission.fields);
      setNotes(submission.notes ?? '');
      setSavedDraft(data);
    } catch (cause) {
      setDraftError(getErrorMessage(cause));
    } finally {
      setDraftLoading(false);
    }
  };
  const draftPath = viewerSlug
    ? `/clinical-trials/investigators/${encodeURIComponent(viewerSlug)}/poster-drafts/${posterTemplateId(posterStyle)}`
    : null;
  useEffect(() => {
    if (!draftPath || demoMode) return;
    if (posterStyle in versions.current) {
      setSavedDraft(versions.current[posterStyle] ?? null);
      setDraftError(null);
      return;
    }
    let active = true;
    setDraftLoading(true);
    setDraftError(null);
    apiClient
      .get<PosterDraft | null>(draftPath)
      .then(({ data }) => {
        if (!active) return;
        versions.current[posterStyle] = data;
        setSavedDraft(data);
        if (data) {
          setFields(data.fields);
          setNotes(data.notes ?? '');
        }
      })
      .catch(cause => {
        if (active) setDraftError(getErrorMessage(cause));
      })
      .finally(() => {
        if (active) setDraftLoading(false);
      });
    return () => {
      active = false;
    };
  }, [apiClient, demoMode, draftPath, posterStyle, reloadDraft]);

  const saveDraft = async () => {
    if (!draftPath || !(posterStyle in versions.current)) return;
    setDraftSaving(true);
    setDraftError(null);
    try {
      const { data } = await apiClient.put<PosterDraft>(draftPath, {
        expectedVersion: versions.current[posterStyle]?.version ?? 0,
        fields,
        notes,
      });
      versions.current[posterStyle] = data;
      setSavedDraft(data);
    } catch (cause) {
      setDraftError(getErrorMessage(cause));
    } finally {
      setDraftSaving(false);
    }
  };

  const updateField = <K extends keyof PosterSubmissionFields>(
    key: K,
    value: PosterSubmissionFields[K]
  ) => {
    setFields(current => ({ ...current, [key]: value, exportedPdfDataUrl: null }));
  };

  const updateListItem = (key: 'bullets' | 'checklist', idx: number, value: string) => {
    setFields(current => {
      const values = [...(current[key] ?? [])];
      values[idx] = value;
      return { ...current, [key]: values, exportedPdfDataUrl: null };
    });
  };

  const selectStyle = (style: PosterStyle) => {
    if (style === posterStyle || draftLoading || draftSaving) return;
    noteDrafts.current[posterStyle] = notes;
    setNotes(noteDrafts.current[style] ?? '');
    drafts.current[posterStyle] = fields;
    setFields(drafts.current[style] ?? initialPosterFields(style, viewerLabel));
    setPosterStyle(style);
    setFocusedText(null);
    setStatus(null);
    setError(null);
  };

  const editPortrait = async () => {
    if (fields.portraitSource) {
      setPortraitEditor(fields.portraitSource);
      return;
    }
    const portraitUrl = fields.bottomImageDataUrl ?? DEFAULT_POSTER_IMAGE;
    const previous = portraitSources.current[posterStyle];
    if (previous && (fields.portraitHidden || previous.rendered === portraitUrl)) {
      setPortraitEditor(previous.source);
      return;
    }
    setLoadingImage(true);
    try {
      const image = new Image();
      image.src = portraitUrl;
      await image.decode();
      setPortraitEditor({
        url: portraitUrl,
        width: image.naturalWidth,
        height: image.naturalHeight,
        crop: DEFAULT_PORTRAIT_CROP,
      });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoadingImage(false);
    }
  };

  const hasPosterSuggestion =
    !!suggestion &&
    suggestion.piSlug === viewerSlug &&
    (suggestion.posterStatus === 'pending' || suggestion.posterStatus === 'applied');

  const applyPosterSuggestion = useCallback(async () => {
    if (!suggestion) return;
    setApplyingSuggestion(true);
    setError(null);
    try {
      const suggestedFields = suggestion.poster.fields;
      let bottomImageDataUrl: string | null | undefined;
      if (suggestedFields.bottomImageUrl) {
        try {
          bottomImageDataUrl = await fetchSuggestionPosterImageDataUrl(apiClient, suggestion.id);
        } catch (err) {
          setError(
            posterT('clinicalTrials.ui.thePosterSuggestionWasAppliedButTheImageCould', {
              value1: getErrorMessage(err),
            })
          );
        }
      }
      setFields(current => ({
        ...current,
        header: suggestedFields.header || current.header,
        subheader: suggestedFields.subheader || current.subheader,
        banner: suggestedFields.banner ?? current.banner,
        bullets: suggestedFields.bullets.length > 0 ? suggestedFields.bullets : current.bullets,
        checklist:
          suggestedFields.checklist.length > 0 ? suggestedFields.checklist : current.checklist,
        bottomText: suggestedFields.bottomText || current.bottomText,
        bottomImageDataUrl: bottomImageDataUrl ?? current.bottomImageDataUrl,
        portraitHidden: bottomImageDataUrl ? false : current.portraitHidden,
        exportedPdfDataUrl: null,
      }));
      if (suggestion.poster.notes && !notes.trim()) {
        setNotes(suggestion.poster.notes);
      }
      setAcceptedSuggestions(current => ({ ...current, [posterStyle]: suggestion.id }));
      setStatus(posterT('clinicalTrials.posterEditor.suggestionApplied'));
      if (canSubmit && !demoMode) {
        await onSuggestionStatusChange?.('poster', 'applied');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setApplyingSuggestion(false);
    }
  }, [apiClient, canSubmit, demoMode, notes, onSuggestionStatusChange, posterStyle, suggestion]);

  const dismissPosterSuggestion = useCallback(async () => {
    if (!suggestion) return;
    setAcceptedSuggestions(current => ({ ...current, [posterStyle]: undefined }));
    try {
      await onSuggestionStatusChange?.('poster', 'dismissed');
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }, [onSuggestionStatusChange, posterStyle, suggestion]);

  const exportPdf = useCallback(async () => {
    setError(null);
    setExporting(true);
    try {
      const pdfDataUrl = await createPosterPdfDataUrl(fields, posterStyle);
      downloadDataUrl(pdfDataUrl, 'Ivelion_poster.pdf');
      setFields(current => ({ ...current, exportedPdfDataUrl: pdfDataUrl }));
      setStatus(posterT('clinicalTrials.posterEditor.pdfCreated'));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  }, [fields, posterStyle]);

  const submit = useCallback(async () => {
    if (!canSubmit || demoMode) {
      setError(posterT('clinicalTrials.ui.thisPosterViewCannotSubmitChanges'));
      return;
    }
    if (!viewerSlug) {
      setError(posterT('clinicalTrials.ui.pleaseSelectAProfileInTheSidebarFirst'));
      return;
    }
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      const exportedPdfDataUrl =
        fields.exportedPdfDataUrl ?? (await createPosterPdfDataUrl(fields, posterStyle));
      await createPosterSubmission({
        selectedInvestigatorSlug: viewerSlug,
        selectedInvestigatorName: investigator?.full_name ?? viewerLabel ?? null,
        selectedInvestigatorEmail: investigator?.email ?? null,
        selectedTemplateIds: [posterTemplateId(posterStyle)],
        fields: { ...fields, exportedPdfDataUrl },
        practices: [],
        notes: notes.trim() || null,
        sourceSuggestionId: acceptedSuggestions[posterStyle],
      });
      setFields(current => ({ ...current, exportedPdfDataUrl }));
      setStatus(posterT('clinicalTrials.posterEditor.submitted'));
      try {
        await onSuggestionRefresh?.();
      } catch {
        // Submission already succeeded; stale suggestion state can recover on reload.
      }
      setAcceptedSuggestions(current => ({ ...current, [posterStyle]: undefined }));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [
    acceptedSuggestions,
    createPosterSubmission,
    canSubmit,
    demoMode,
    fields,
    investigator,
    notes,
    posterStyle,
    onSuggestionRefresh,
    viewerLabel,
    viewerSlug,
  ]);

  const busy =
    draftLoading || draftSaving || saving || exporting || applyingSuggestion || loadingImage;
  const blocks = posterTextBlocks(fields, posterStyle);

  return (
    <div
      className="@container flex h-full min-h-0 min-w-0 flex-col bg-muted/20"
      data-onboarding-id="clinical-trials-poster"
      data-ai-id="clinical-trials.poster"
      data-ai-label="Poster"
      data-ai-type="section"
      data-ai-capabilities="highlight,waitForClick"
    >
      {!demoMode && (
        <div
          className="flex flex-wrap items-center gap-3 border-b p-3 text-sm"
          role="region"
          aria-label="Saved poster draft"
        >
          <Button
            variant="outline"
            onClick={() => void saveDraft()}
            disabled={!canSubmit || busy || !(posterStyle in versions.current)}
          >
            {draftSaving ? t('clinicalTrials.ui.savingDraft') : t('clinicalTrials.ui.saveDraft')}
          </Button>
          <span>
            {draftLoading
              ? t('clinicalTrials.ui.loadingDraft')
              : savedDraft
                ? t('clinicalTrials.ui.savedDraftVersion', {
                    version: savedDraft.version,
                    time: new Date(savedDraft.updatedAt).toLocaleString(),
                    actor: savedDraft.updatedBy,
                  })
                : t('clinicalTrials.ui.noSavedDraft')}
          </span>
          {draftError && (
            <div role="alert">
              {draftError}{' '}
              <Button
                variant="outline"
                onClick={() => {
                  delete versions.current[posterStyle];
                  setReloadDraft(value => value + 1);
                }}
              >
                {t('clinicalTrials.ui.reopenSavedDraft')}
              </Button>
            </div>
          )}
        </div>
      )}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b bg-background px-4 py-3">
        <div>
          <h2 className="text-lg font-semibold">{posterT('clinicalTrials.posterEditor.title')}</h2>
        </div>
        <div
          className="flex rounded-3xl border bg-background p-1"
          role="group"
          aria-label={posterT('clinicalTrials.posterEditor.view')}
        >
          <Button
            variant="ghost"
            size="sm"
            aria-pressed={enlarged}
            onClick={() => setEnlarged(current => !current)}
            className="gap-2"
          >
            {enlarged ? <Scan className="h-3.5 w-3.5" /> : <ZoomIn className="h-3.5 w-3.5" />}
            {posterT(
              enlarged ? 'clinicalTrials.posterEditor.fit' : 'clinicalTrials.posterEditor.enlarge'
            )}
          </Button>
          <Button
            variant={editing ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setEditing(true)}
            aria-pressed={editing}
            className="gap-2"
          >
            <Pencil className="h-3.5 w-3.5" />
            {posterT('clinicalTrials.posterEditor.edit')}
          </Button>
          <Button
            variant={!editing ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => {
              setEditing(false);
              setFocusedText(null);
            }}
            aria-pressed={!editing}
            className="gap-2"
          >
            <Eye className="h-3.5 w-3.5" />
            {posterT('clinicalTrials.posterEditor.preview')}
          </Button>
        </div>
      </div>

      <div className="shrink-0 space-y-2 px-4">
        {demoMode && (
          <div className="rounded-3xl border border-primary/20 bg-primary/5 p-3 text-xs">
            <div className="font-medium text-foreground">
              {posterT('clinicalTrials.ui.demoPreview')}
            </div>
            <p className="mt-1 text-muted-foreground">
              {posterT('clinicalTrials.ui.youCanEditThePosterAndExportItAs')}
            </p>
          </div>
        )}
        {operatorMode && (
          <div className="rounded-3xl border border-primary/20 bg-primary/5 p-3 text-xs">
            <div className="font-medium text-foreground">
              {posterT('clinicalTrials.ui.clinicalTrialsAdminMode')}
            </div>
            <p className="mt-1 text-muted-foreground">
              {posterT('clinicalTrials.ui.thePosterIsSubmittedUnderYourAccountForThe')}
            </p>
          </div>
        )}
        {hasPosterSuggestion && (
          <div className="rounded-3xl border border-primary/20 bg-primary/5 p-3">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-3xl bg-primary/10 text-primary">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-foreground">
                  {posterT('clinicalTrials.ui.posterSuggestionAvailable')}
                </div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {posterT('clinicalTrials.ui.acceptingTheSuggestionOnlyUpdatesTheDraftYouCan')}
                </p>
                <div className="mt-3 flex gap-2">
                  {canSubmit && !demoMode && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void dismissPosterSuggestion()}
                      disabled={suggestionUpdating || busy}
                    >
                      {posterT('clinicalTrials.ui.reject')}
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void applyPosterSuggestion()}
                    disabled={suggestionUpdating || busy}
                  >
                    {applyingSuggestion
                      ? posterT('clinicalTrials.ui.applying')
                      : demoMode
                        ? posterT('clinicalTrials.ui.applyToPreview')
                        : posterT('clinicalTrials.ui.apply')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={styles.workspace}>
        <div
          className={styles.canvasArea}
          data-onboarding-id="clinical-trials-poster-editor"
          data-ai-id="clinical-trials.poster-editor"
          data-ai-label="Poster Editor"
          data-ai-type="section"
          data-ai-capabilities="highlight,waitForClick"
        >
          <div className="flex shrink-0 flex-wrap items-center justify-center gap-2 px-3 py-2 text-center text-xs text-muted-foreground">
            {editing ? <Pencil className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {posterT(
              editing
                ? fields.portraitHidden
                  ? 'clinicalTrials.posterPortrait.withoutPhotoHint'
                  : 'clinicalTrials.posterEditor.editHint'
                : 'clinicalTrials.posterEditor.previewHint'
            )}
            {editing && !draftLoading && fields.portraitHidden && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                className="gap-2"
                onClick={() => void editPortrait()}
              >
                <ImagePlus className="h-4 w-4" />
                {posterT('clinicalTrials.posterPortrait.add')}
              </Button>
            )}
          </div>
          <div
            className={`${styles.preview} ${enlarged ? styles.enlarged : ''}`}
            data-onboarding-id="clinical-trials-poster-preview"
            data-ai-id="clinical-trials.poster-preview"
            data-ai-label="Poster Preview"
            data-ai-type="section"
            data-ai-capabilities="highlight,waitForClick"
          >
            <div
              className={`${styles.poster} relative aspect-[1587/2245] bg-white shadow-lg`}
              style={{ containerType: 'inline-size' }}
            >
              <PosterArtwork
                fields={fields}
                style={posterStyle}
                hiddenTextKey={editing ? focusedText : null}
              />
              {editing && !draftLoading && (
                <fieldset disabled={busy} className="absolute inset-0 m-0 min-w-0 border-0 p-0">
                  <legend className="sr-only">
                    {posterT('clinicalTrials.posterEditor.editHint')}
                  </legend>
                  {blocks.map(block => {
                    const { size } = fitPosterText(block);
                    return (
                      <textarea
                        key={block.key}
                        aria-label={block.label}
                        value={block.text}
                        spellCheck={false}
                        onFocus={() => setFocusedText(block.key)}
                        onBlur={() => setFocusedText(null)}
                        onChange={event =>
                          block.index === undefined
                            ? updateField(
                                block.field as 'header' | 'subheader' | 'banner' | 'bottomText',
                                event.target.value
                              )
                            : updateListItem(
                                block.field as 'bullets' | 'checklist',
                                block.index,
                                event.target.value
                              )
                        }
                        className={`${styles.text} absolute resize-none overflow-hidden rounded-3xl border border-dashed border-sky-600/20 bg-transparent outline-none transition-colors hover:border-sky-600 hover:bg-sky-400/5 focus:z-10 focus:border-solid focus:border-sky-600 focus:ring-2 focus:ring-sky-500/30`}
                        style={
                          {
                            left: `${((block.x - 32) / POSTER_W) * 100}%`,
                            top: `${((block.y - 12) / POSTER_H) * 100}%`,
                            width: `${((block.width + 64) / POSTER_W) * 100}%`,
                            height: `${((block.height + 24) / POSTER_H) * 100}%`,
                            '--poster-font-size': `${(size / POSTER_W) * 100}cqw`,
                            padding: `${(12 / POSTER_W) * 100}cqw ${(32 / POSTER_W) * 100}cqw`,
                            fontFamily: posterBlockFont(block),
                            fontWeight: block.weight,
                            lineHeight: block.lineHeight ?? 1.25,
                            letterSpacing: `${((block.letterSpacing ?? 0) / POSTER_W) * 100}cqw`,
                            textAlign: block.align,
                            color: focusedText === block.key ? block.color : 'transparent',
                            caretColor: block.color,
                          } as CSSProperties
                        }
                      />
                    );
                  })}
                  {!fields.portraitHidden && (
                    <div
                      data-portrait-edit-frame
                      className="pointer-events-none absolute rounded-full border-2 border-sky-600 ring-2 ring-white/90"
                      style={{
                        borderRadius: '50%',
                        left: `${(PORTRAIT_FRAME.x / POSTER_W) * 100}%`,
                        top: `${(PORTRAIT_FRAME.y / POSTER_H) * 100}%`,
                        width: `${(PORTRAIT_FRAME.width / POSTER_W) * 100}%`,
                        height: `${(PORTRAIT_FRAME.height / POSTER_H) * 100}%`,
                      }}
                    >
                      <button
                        type="button"
                        aria-label={posterT('clinicalTrials.posterPortrait.title')}
                        title={posterT('clinicalTrials.posterPortrait.title')}
                        onClick={() => void editPortrait()}
                        className="pointer-events-auto absolute right-0 top-[15%] flex h-9 w-9 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full border-2 border-white bg-sky-600 text-white shadow-md hover:bg-sky-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </fieldset>
              )}
            </div>
          </div>
        </div>
        <aside className={styles.rail} aria-label={posterT('clinicalTrials.posterEditor.styles')}>
          <div className={styles.railIntro}>
            <h3 className="text-sm font-semibold">
              {posterT('clinicalTrials.posterEditor.styles')}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {posterT('clinicalTrials.posterEditor.chooseStyle')}
            </p>
          </div>
          <div
            className={styles.designs}
            role="group"
            aria-label={posterT('clinicalTrials.posterEditor.styles')}
          >
            {POSTER_STYLES.map(style => (
              <button
                key={style}
                type="button"
                aria-label={posterT(`clinicalTrials.posterStyles.${style}.name`)}
                aria-pressed={posterStyle === style}
                disabled={busy}
                onClick={() => selectStyle(style)}
                className={`${styles.design} min-w-0 rounded-3xl border p-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${posterStyle === style ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border bg-background hover:bg-accent'}`}
              >
                <div
                  className={`${styles.thumbnail} flex justify-center overflow-hidden rounded-3xl bg-muted/60 p-1.5`}
                  aria-hidden="true"
                >
                  <div className="aspect-[1587/2245] h-full overflow-hidden shadow-sm">
                    <PosterArtwork
                      fields={
                        style === posterStyle
                          ? fields
                          : (drafts.current[style] ?? initialPosterFields(style, viewerLabel))
                      }
                      style={style}
                    />
                  </div>
                </div>
                <div className={styles.designCopy}>
                  <div className="flex items-center justify-between gap-1 text-xs font-medium">
                    {posterT(`clinicalTrials.posterStyles.${style}.name`)}
                    {posterStyle === style && (
                      <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                    )}
                  </div>
                  <p className="pt-1 text-[11px] leading-snug text-muted-foreground">
                    {posterT(`clinicalTrials.posterStyles.${style}.description`)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </aside>
      </div>
      <div className="shrink-0 border-t bg-background p-3 @lg:px-6">
        {canSubmit && !demoMode && (
          <details className="mb-2">
            <summary className="cursor-pointer text-xs text-muted-foreground">
              {posterT('clinicalTrials.posterEditor.notesToggle')}
            </summary>
            <div className="mt-2 space-y-2">
              <label htmlFor="poster-notes" className="text-xs font-medium">
                {posterT('clinicalTrials.ui.noteForTheIvelionTeam')}
              </label>
              <Textarea
                id="poster-notes"
                rows={2}
                value={notes}
                disabled={busy}
                onChange={event => setNotes(event.target.value)}
                placeholder={posterT('clinicalTrials.ui.optionalNoteAboutThisPosterVersion')}
              />
            </div>
          </details>
        )}
        {error && (
          <div role="alert" className="mb-2 text-xs text-destructive">
            {error}
          </div>
        )}
        {status && (
          <div role="status" className="mb-2 text-xs text-green-700 dark:text-green-400">
            {status}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="mr-auto hidden text-xs text-muted-foreground @lg:inline">
            {posterT('clinicalTrials.posterEditor.printFormat')}
          </span>
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            onClick={() => void exportPdf()}
            disabled={busy}
            data-onboarding-id="clinical-trials-poster-export"
            data-ai-id="clinical-trials.poster-export"
            data-ai-label="Export Poster PDF"
            data-ai-type="button"
            data-ai-capabilities="highlight,waitForClick,externalEffect"
          >
            <Download className="h-4 w-4" />
            {posterT(
              exporting ? 'clinicalTrials.posterEditor.exporting' : 'clinicalTrials.ui.exportPdf'
            )}
          </Button>
          {canSubmit && !demoMode && (
            <Button
              type="button"
              className="gap-2"
              onClick={() => void submit()}
              disabled={busy}
              data-onboarding-id="clinical-trials-poster-submit"
              data-ai-id="clinical-trials.poster-submit"
              data-ai-label="Submit Poster"
              data-ai-type="button"
              data-ai-capabilities="highlight,waitForClick,submitsData,mutatesData"
            >
              <Send className="h-4 w-4" />
              {posterT(saving ? 'clinicalTrials.ui.sending' : 'clinicalTrials.ui.submit')}
            </Button>
          )}
        </div>
      </div>
      {!demoMode && (
        <details className="border-t p-3">
          <summary>{t('clinicalTrials.ui.submittedVersions')}</summary>
          {historyError && <p role="alert">{historyError}</p>}
          {submissions.map(row => (
            <div key={row.id} className="flex flex-wrap items-center gap-3 p-2 text-sm">
              <span>
                {row.fields.header} · {row.status} · {new Date(row.createdAt).toLocaleString()}
              </span>
              <Button
                variant="outline"
                disabled={!canSubmit || busy}
                onClick={() => void editSubmission(row)}
              >
                {t('clinicalTrials.ui.editSubmittedDraft')}
              </Button>
            </div>
          ))}
        </details>
      )}
      {portraitEditor && (
        <PosterPortraitEditor
          source={portraitEditor}
          onCancel={() => setPortraitEditor(null)}
          onRemove={
            fields.portraitHidden
              ? undefined
              : () => {
                  setFields(current => ({
                    ...current,
                    portraitHidden: true,
                    bottomImageDataUrl: null,
                    exportedPdfDataUrl: null,
                  }));
                  setPortraitEditor(null);
                }
          }
          onApply={(url, source) => {
            portraitSources.current[posterStyle] = { rendered: url, source };
            const isDefault =
              source.url === DEFAULT_POSTER_IMAGE &&
              source.crop.zoom === 1 &&
              source.crop.x === 0 &&
              source.crop.y === 0;
            setFields(current => ({
              ...current,
              bottomImageDataUrl: isDefault ? null : url,
              portraitSource: isDefault ? null : source,
              portraitHidden: false,
              exportedPdfDataUrl: null,
            }));
            setPortraitEditor(null);
          }}
        />
      )}
    </div>
  );
}

async function fetchSuggestionPosterImageDataUrl(
  apiClient: import('./context').ClinicalEditorApi,
  suggestionId: string
): Promise<string> {
  const { data } = await apiClient.get<Blob>(
    `/clinical-trials/pi-suggestions/${suggestionId}/poster-image`,
    { responseType: 'blob' }
  );
  return blobToDataUrl(data);
}

async function createPosterPdfDataUrl(
  fields: PosterSubmissionFields,
  style: PosterStyle
): Promise<string> {
  const canvas = await renderPosterCanvas(fields, style);
  const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.98);
  const blob = pdfBlobFromJpegDataUrl(jpegDataUrl, canvas.width, canvas.height);
  return blobToDataUrl(blob);
}

function pdfBlobFromJpegDataUrl(dataUrl: string, imageWidth: number, imageHeight: number): Blob {
  const imageBytes = base64ToBytes(dataUrl.split(',')[1] ?? '');
  const enc = new TextEncoder();
  const chunks: BlobPart[] = [];
  const offsets: number[] = [0];
  let size = 0;
  const push = (value: string | Uint8Array) => {
    const bytes = typeof value === 'string' ? enc.encode(value) : value;
    chunks.push(
      bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
    );
    size += bytes.length;
  };
  const obj = (body: string | Uint8Array, prefix = '', suffix = '') => {
    offsets.push(size);
    push(`${offsets.length - 1} 0 obj\n${prefix}`);
    push(body);
    push(`${suffix}\nendobj\n`);
  };

  push('%PDF-1.4\n');
  obj('<< /Type /Catalog /Pages 2 0 R >>\n');
  obj('<< /Type /Pages /Kids [3 0 R] /Count 1 >>\n');
  obj(
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${A4_W} ${A4_H}] /Resources << /XObject << /Im0 5 0 R >> >> /Contents 4 0 R >>\n`
  );
  const content = `q\n${A4_W} 0 0 ${A4_H} 0 0 cm\n/Im0 Do\nQ\n`;
  obj(content, `<< /Length ${content.length} >>\nstream\n`, 'endstream');
  obj(
    imageBytes,
    `<< /Type /XObject /Subtype /Image /Width ${imageWidth} /Height ${imageHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${imageBytes.length} >>\nstream\n`,
    '\nendstream'
  );
  const xrefOffset = size;
  push(`xref\n0 ${offsets.length}\n0000000000 65535 f \n`);
  for (let i = 1; i < offsets.length; i++) {
    push(`${String(offsets[i]).padStart(10, '0')} 00000 n \n`);
  }
  push(`trailer\n<< /Size ${offsets.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);
  return new Blob(chunks, { type: 'application/pdf' });
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
